import { useLoading } from "context/LoadingContext";
import { ToastContext } from "context/ToastContext";
import { prepareCodesForSubmit, CodeRangeError } from "helpers/codeRanges";
import useDocumentTitle from "hooks/useDocumentTitle";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCreateExplodingFeeScheduleMutation } from "store/queries/explodingFeeSchedule";

import {
  ExplodingFeeScheduleForm,
  IExplodingFeeScheduleFormData,
  INITIAL_FORM_DATA,
} from "./components/ExplodingFeeScheduleForm";
import { scopeRequestFields } from "./helpers/ruleApplicability";

export function CreateExplodingFeeSchedulePage() {
  useDocumentTitle("Create Exploding Fee Schedule");
  const navigate = useNavigate();
  const { setIsLoading } = useLoading();
  const { current: toastElement } = useContext(ToastContext);
  const [createRuleset] = useCreateExplodingFeeScheduleMutation();
  const [formData, setFormData] =
    useState<IExplodingFeeScheduleFormData>(INITIAL_FORM_DATA);

  const handleSubmit = async () => {
    let cptCodes: string[];
    try {
      cptCodes = prepareCodesForSubmit(formData.cptCodes);
    } catch (err) {
      toastElement?.show({
        severity: "error",
        summary: "Invalid code range",
        detail:
          err instanceof CodeRangeError
            ? err.message
            : "Failed to expand code ranges",
      });
      return;
    }
    setIsLoading(true);
    try {
      await createRuleset({
        // CLE-2842 step 2 — a Fallback rule has no provider and an optional payer; a
        // Payer-Provider rule's payer may be null (All payers).
        ...scopeRequestFields(formData),
        cptCodes,
        totalRateInCents: formData.totalRateInCents!,
        groupName: formData.groupName.trim() || undefined,
        contractLanguage: formData.contractLanguage.trim() || null,
        // CLE-2524: applicability wins over the legacy isDefault flag.
        applicability: formData.applicability,
      }).unwrap();

      toastElement?.show({
        severity: "success",
        summary: "Created!",
        detail: "Exploding fee schedule ruleset created successfully.",
      });
      navigate("/payer-rules");
    } catch (error: any) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail:
          error?.data?.message ||
          "Failed to create exploding fee schedule ruleset",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ExplodingFeeScheduleForm
      title="Create Exploding Fee Schedule Ruleset"
      submitLabel="Create Ruleset"
      submitIcon="pi pi-check"
      formData={formData}
      onChange={setFormData}
      onSubmit={handleSubmit}
    />
  );
}
